// Dados da tela de início de Atender (versão aprovada em 12/09/2026): a
// fila ao vivo (números e as conversas que esperam, para o celular do
// destaque), os chips por caixa, o gráfico de conversas por dia com a semana
// anterior para a variação, o tempo de primeira resposta e o CSAT
// (administrador). Só chamadas que já existem; falha vira null, nunca zero.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import ReportsAPI from 'dashboard/api/reports';
import CSATReportsAPI from 'dashboard/api/csatReports';
import {
  waitingDurationFromMinutes,
  waitingMinutesFromEpoch,
} from 'dashboard/helper/waitingLabel';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const DAY = 86_400;
const MAX_QUEUE_ROWS = 6;
const MAX_RECENT_ROWS = 5;
const MAX_INBOX_CHIPS = 5;
export const EMPTY = '·';

const initials = name =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toLocaleUpperCase())
    .join('');

const lastText = chat =>
  chat?.last_non_activity_message?.content ||
  (chat?.messages || []).at(-1)?.content ||
  '';

// Um relatório diário chega como [{ value, timestamp }], um ponto por dia,
// com o timestamp na meia-noite do FUSO DA CONTA (o backend preenche os dias
// sem conversa com 0). Os dias saem desses pontos, não de um corte por
// meia-noite UTC: em Brasília o corte UTC cai às 21h, rotulava cada barra com
// o dia anterior e zerava "hoje" depois das 21h.
const sortedDays = series =>
  (series || [])
    .filter(point => Number.isFinite(Number(point?.timestamp)))
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

// Meio do dia do ponto: o rótulo não vira o dia vizinho quando o navegador
// está em outro fuso que a conta.
const middayOf = point => new Date((Number(point.timestamp) + DAY / 2) * 1000);

const sumValues = points =>
  points.reduce((acc, point) => acc + (Number(point.value) || 0), 0);

export const deltaLabel = (current, previous, formatter = value => value) => {
  if (current === null || previous === null || previous === undefined)
    return '';
  if (!previous) return '';
  const change = Math.round(((current - previous) / previous) * 100);
  if (!change) return '';
  return formatter(change);
};

export function useAttendanceHome() {
  const { t, locale } = useI18n();
  const store = useStore();
  const conversationStats = useMapGetter('conversationStats/getStats');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const currentRole = useMapGetter('getCurrentRole');
  const teamChatUnread = useMapGetter('teamChat/getUnreadCount');

  const queue = ref({ unassigned: [], mine: [], loaded: false });
  const queueError = ref(false);
  const series = ref(null);
  // Relatório negado (atendente sem permissão de relatórios) não é semana
  // zerada: o gráfico some e a tela diz que não está liberado.
  const seriesForbidden = ref(false);
  const summary = ref({ current: null, previous: null });
  const csat = ref(null);

  const intl = toIntlLocale(locale.value);
  const weekday = new Intl.DateTimeFormat(intl, { weekday: 'short' });

  const mineCount = computed(() => conversationStats.value?.mineCount || 0);
  const unassignedCount = computed(
    () => conversationStats.value?.unAssignedCount || 0
  );
  const allCount = computed(() => conversationStats.value?.allCount || 0);
  const isAdministrator = computed(() => currentRole.value === 'administrator');

  // As conversas do celular: sem ninguém primeiro (mais antiga no topo),
  // depois as suas.
  const queueRows = computed(() => {
    const row = (chat, mine) => {
      const minutes =
        Number(chat.waiting_since) > 0
          ? Math.max(0, waitingMinutesFromEpoch(chat.waiting_since))
          : null;
      return {
        id: chat.id,
        name: chat.meta?.sender?.name || t('HUB.ATTENDANCE.UNKNOWN_CONTACT'),
        initials: initials(chat.meta?.sender?.name),
        text: lastText(chat),
        mine,
        minutes,
        wait: minutes === null ? '' : waitingDurationFromMinutes(minutes, t),
      };
    };
    const unassigned = [...queue.value.unassigned]
      .sort(
        (a, b) => Number(a.waiting_since || 0) - Number(b.waiting_since || 0)
      )
      .map(chat => row(chat, false));
    const mine = queue.value.mine.map(chat => row(chat, true));
    return [...unassigned, ...mine].slice(0, MAX_QUEUE_ROWS);
  });

  const recentRows = computed(() => {
    const unique = new Map(
      [...queue.value.unassigned, ...queue.value.mine].map(chat => [
        chat.id,
        chat,
      ])
    );
    return [...unique.values()]
      .sort(
        (a, b) =>
          Number(b.last_activity_at || 0) - Number(a.last_activity_at || 0)
      )
      .slice(0, MAX_RECENT_ROWS)
      .map(chat => ({
        id: chat.id,
        name: chat.meta?.sender?.name || t('HUB.ATTENDANCE.UNKNOWN_CONTACT'),
        initials: initials(chat.meta?.sender?.name),
        avatar: chat.meta?.sender?.thumbnail || '',
        text: lastText(chat),
        unread: Number(chat.unread_count) || 0,
        time: chat.last_activity_at
          ? new Intl.DateTimeFormat(intl, {
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(Number(chat.last_activity_at) * 1000))
          : '',
      }));
  });

  const longestWaiting = computed(() => {
    const waiting = queueRows.value.filter(item => item.minutes !== null);
    if (!waiting.length) return null;
    return waiting.reduce((oldest, item) =>
      item.minutes > oldest.minutes ? item : oldest
    );
  });

  // Últimos 7 dias, um valor por dia, com o total da semana anterior. O
  // último ponto é hoje no fuso da conta.
  const chart = computed(() => {
    if (!series.value) return null;
    const points = sortedDays(series.value);
    const week = points.slice(-7);
    const days = week.map((point, index) => ({
      key: String(point.timestamp),
      label:
        index === week.length - 1
          ? t('HUB.ATTENDANCE.TODAY')
          : weekday.format(middayOf(point)),
      value: Number(point.value) || 0,
    }));
    const max = Math.max(1, ...days.map(day => day.value));
    const weekTotal = sumValues(week);
    const previousTotal = sumValues(points.slice(-14, -7));
    return {
      days: days.map(day => ({
        ...day,
        height: Math.round((day.value / max) * 100),
      })),
      weekTotal,
      previousTotal,
      weekDelta: deltaLabel(weekTotal, previousTotal, change =>
        t('HUB.ATTENDANCE.DELTA_PERCENT', {
          value: change > 0 ? `+${change}` : change,
        })
      ),
    };
  });

  const firstResponse = computed(() => {
    const current = summary.value.current;
    if (current === null) return { label: EMPTY, delta: '', hint: '' };
    const minutes = Math.round(current / 60);
    const previous = summary.value.previous;
    let delta = '';
    if (previous) {
      const diff = Math.round((current - previous) / 60);
      if (diff) {
        delta = t('HUB.ATTENDANCE.DELTA_MINUTES', {
          value: diff > 0 ? `+${diff}` : diff,
        });
      }
    }
    return {
      label: t('HUB.ATTENDANCE.MINUTES', { count: minutes }),
      delta,
      slower: previous ? current > previous : false,
    };
  });

  const inboxChips = computed(() =>
    (inboxes.value || []).slice(0, MAX_INBOX_CHIPS).map(inbox => ({
      id: inbox.id,
      label: inbox.name,
      icon: getInboxIconByType(inbox.channel_type, inbox.medium, 'line'),
    }))
  );

  const loadQueue = async () => {
    queueError.value = false;
    const fetchList = async assigneeType => {
      const { data } = await ConversationAPI.get({
        assigneeType,
        status: 'open',
        page: 1,
        sortBy: 'last_activity_at_desc',
      });
      return data?.data?.payload ?? data?.payload ?? [];
    };
    try {
      const [unassigned, mine] = await Promise.all([
        fetchList('unassigned'),
        fetchList('me'),
      ]);
      queue.value = { unassigned, mine, loaded: true };
    } catch {
      queueError.value = true;
      queue.value = { unassigned: [], mine: [], loaded: true };
    }
  };

  const loadSeries = async () => {
    const to = Math.floor(Date.now() / 1000);
    const from = to - 14 * DAY;
    try {
      const { data } = await ReportsAPI.getReports({
        metric: 'conversations_count',
        from,
        to,
        groupBy: 'day',
      });
      series.value = Array.isArray(data) ? data : [];
      seriesForbidden.value = false;
    } catch (error) {
      // Falha vira "·", nunca 7 barras em 0.
      series.value = null;
      const status = error?.response?.status;
      seriesForbidden.value = status === 401 || status === 403;
    }
    try {
      const { data } = await ReportsAPI.getSummary(to - 7 * DAY, to);
      summary.value = {
        current: Number(data?.avg_first_response_time) || 0,
        previous: Number(data?.previous?.avg_first_response_time) || null,
      };
    } catch {
      summary.value = { current: null, previous: null };
    }
  };

  const loadCsat = async () => {
    if (!isAdministrator.value) return;
    try {
      const to = Math.floor(Date.now() / 1000);
      const { data } = await CSATReportsAPI.getMetrics({
        from: to - 30 * DAY,
        to,
      });
      const total = Number(data?.total_count) || 0;
      if (!total) return;
      const sum = Object.entries(data?.ratings_count || {}).reduce(
        (acc, [rating, amount]) => acc + Number(rating) * Number(amount),
        0
      );
      csat.value = (sum / total).toLocaleString(intl, {
        maximumFractionDigits: 1,
      });
    } catch {
      csat.value = null;
    }
  };

  onMounted(() => {
    store
      .dispatch('conversationStats/get', {
        assigneeType: 'me',
        status: 'active',
      })
      .catch(() => {});
    store.dispatch('inboxes/get').catch(() => {});
    store.dispatch('teamChat/fetchUnreadCount').catch(() => {});
    loadQueue();
    loadSeries();
    loadCsat();
  });

  return {
    mineCount,
    unassignedCount,
    allCount,
    queueRows,
    recentRows,
    queueError,
    reloadQueue: loadQueue,
    queueLoaded: computed(() => queue.value.loaded),
    longestWaiting,
    chart,
    seriesForbidden,
    firstResponse,
    csat,
    inboxChips,
    teamChatUnread: computed(() => Number(teamChatUnread.value) || 0),
  };
}
